// ─────────────────────────────────────────────────────────────────────────────
// Agent tools catalog
// ─────────────────────────────────────────────────────────────────────────────
// This array is sent to the Claude Messages API (via the chat-agent edge
// function) on every turn. It must stay in sync with `executeAgentTool` in
// VideoEditor.tsx — every tool declared here must have a matching case in
// the executor, and vice-versa.
//
// Convention:
//  - `prefill_*` tools OPEN a UI panel and populate its fields; they DO NOT
//    trigger AI generation. The user reviews and clicks "Generar" manually.
//  - `get_*` / `list_*` tools are read-only and safe.
//  - Everything else mutates the timeline/project state directly (reversible
//    via Undo in the editor).

export type AgentTool = {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
};

export const AGENT_TOOLS: AgentTool[] = [
  // ── Interactive Q&A ─────────────────────────────────────────────────────
  {
    name: 'ask_user',
    description:
      'Hace una o más preguntas al usuario mostrando opciones clickeables inline en el chat. El usuario puede elegir opciones predefinidas, escribir una respuesta libre, o una combinación. Devuelve las respuestas estructuradas como JSON keyed por el id de cada pregunta. USALA SIEMPRE antes de responder en texto con preguntas; el usuario prefiere clickear opciones a tipear. Úsala especialmente al inicio de una tarea (público, plataforma, objetivo, duración) y cuando necesites confirmar decisiones.',
    input_schema: {
      type: 'object',
      properties: {
        questions: {
          type: 'array',
          minItems: 1,
          maxItems: 6,
          items: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description:
                  "Identificador corto y único (snake_case) para referenciar esta pregunta en la respuesta. Ej: 'target_audience', 'platform', 'duration'.",
              },
              question: {
                type: 'string',
                description: 'Texto de la pregunta tal como lo verá el usuario.',
              },
              type: {
                type: 'string',
                enum: ['single', 'multi', 'text'],
                description:
                  "'single' = elegir UNA opción. 'multi' = elegir varias. 'text' = respuesta libre sin opciones.",
              },
              options: {
                type: 'array',
                items: { type: 'string' },
                description:
                  "Opciones predefinidas. Requerido si type='single' o 'multi'. Ignorado si type='text'. Mantené las opciones cortas (1-6 palabras).",
              },
              allowCustom: {
                type: 'boolean',
                description:
                  'Si true (default), el usuario también puede escribir una opción custom además de las predefinidas. Poné false si querés forzar elección entre las opciones.',
              },
            },
            required: ['id', 'question', 'type'],
          },
        },
      },
      required: ['questions'],
    },
  },
  {
    name: 'request_user_upload',
    description:
      'Abre un panel dedicado en la sidebar izquierda pidiéndole al usuario que suba uno o más archivos (típicamente fotos de producto, del negocio, del local, de una persona/modelo, packaging, referencias de marca, etc.) y espera a que confirme. Usala CADA VEZ que necesites material real del usuario antes de prefillear una escena o imagen IA — una foto real siempre va a rendir mucho mejor que una descripción textual. Esta tool BLOQUEA hasta que el usuario confirma. CUANDO EL USUARIO CONFIRMA, la tool devuelve directamente: (1) un bloque de texto con los assetIds y nombres, y (2) cada IMAGEN subida inline para que vos la VEAS al instante (no necesitás llamar a ninguna otra tool para observar imágenes). Si el usuario sube videos, vienen listados por nombre — para verlos, llamá a analyze_video_quick con el assetId. Si el usuario cancela o cierra el panel sin subir nada, devuelve { cancelled: true }. Podés personalizar el mensaje explicativo y el texto del botón de confirmación.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto explicativo que se muestra arriba del área de subida. Sé específico sobre QUÉ archivos necesitás y POR QUÉ (ej: "Necesito una foto del bolso modelo X en buena luz, preferiblemente sobre fondo neutro, para usarla como referencia de producto en la escena 1.").',
        },
        buttonLabel: {
          type: 'string',
          description:
            'Texto del botón de confirmación al pie del panel (ej: "Usar estas imágenes", "Continuar", "Listo"). Mantenelo corto (1-4 palabras).',
        },
        accept: {
          type: 'string',
          enum: ['image', 'video', 'image+video'],
          description:
            'Qué tipos de archivo aceptar. Default: "image". Usá "video" solo si pedís específicamente un video de referencia.',
        },
      },
      required: ['message', 'buttonLabel'],
    },
  },

  // ── Read-only context ──────────────────────────────────────────────────
  {
    name: 'get_project_context',
    description:
      'Devuelve información general del proyecto de video actual: nombre, duración total en segundos, aspect ratio, y recuento de clips por tipo. Llamala al inicio de una conversación para entender el estado del proyecto antes de proponer acciones.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'list_timeline_clips',
    description:
      'Lista todos los clips en la línea de tiempo con sus propiedades (id, tipo, nombre, startTime, duration, trackIndex, prompt de IA y estado de generación si aplica). Úsala cuando necesites razonar sobre el orden, duraciones o contenido ya presente en la timeline.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'list_project_assets',
    description:
      'Lista los assets (videos, audios, imágenes) subidos o generados que están disponibles en el proyecto. Úsala para descubrir material existente que se pueda reutilizar como referencia, primer frame, último frame o para insertar directamente en la timeline.',
    input_schema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['video', 'audio', 'image'],
          description: 'Filtrá por tipo de asset. Omitir para listar todos.',
        },
      },
    },
  },
  {
    name: 'analyze_video_quick',
    description:
      'Análisis VISUAL RÁPIDO de un asset de tipo VIDEO del proyecto. Extrae varios frames distribuidos uniformemente del video y te los devuelve como imágenes para que vos (Claude) los observes directamente. Es rápido pero NO incluye audio ni movimiento entre frames. SOLO sirve para videos — NO la uses sobre assets de tipo imagen (las imágenes ya las recibís inline cuando se suben con request_user_upload, o usá list_generated_images para imágenes generadas con IA). Úsala cuando el usuario pregunte por el contenido visual de un video y unos frames clave alcanzan para responder. Si necesitás entender audio/diálogo/música o secuencia temporal precisa, usá analyze_media_deep.',
    input_schema: {
      type: 'object',
      properties: {
        assetId: { type: 'string', description: 'ID del asset (debe ser de tipo video).' },
        frameCount: {
          type: 'integer',
          minimum: 3,
          maximum: 12,
          description: 'Cantidad de frames a extraer. Default: 6.',
        },
      },
      required: ['assetId'],
    },
  },
  {
    name: 'analyze_media_deep',
    description:
      "Análisis PROFUNDO de uno o varios assets de tipo VIDEO y/o AUDIO del proyecto usando Gemini (que sí entiende video y audio nativamente). Mandale los assets junto con una pregunta puntual y Gemini devuelve la respuesta en texto. SOLO sirve para video y audio — NO la uses sobre imágenes. Úsala cuando necesites entender narrativa, diálogo, música, efectos sonoros, secuencia temporal precisa o detalles que no se ven en frames sueltos. Es más lento que analyze_video_quick — preferí quick para preguntas visuales rápidas sobre video.",
    input_schema: {
      type: 'object',
      properties: {
        assetIds: {
          type: 'array',
          items: { type: 'string' },
          minItems: 1,
          maxItems: 5,
          description: 'IDs de los assets (video y/o audio) a analizar juntos.',
        },
        prompt: {
          type: 'string',
          minLength: 3,
          description: 'Pregunta o instrucción específica para Gemini sobre los archivos. Sé concreto.',
        },
      },
      required: ['assetIds', 'prompt'],
    },
  },
  {
    name: 'generate_transcript',
    description:
      'Genera (o reutiliza el cacheado) el transcript palabra por palabra de un clip de video o audio de la timeline. Llamala cuando necesites saber con precisión QUÉ se dice y EN QUÉ MOMENTO en un clip — por ejemplo para encontrar una frase puntual, decidir dónde cortar, o sincronizar texto. Internamente transcribe el archivo *fuente completo* (no sólo la parte visible del clip) y guarda el resultado en el clip; llamadas siguientes sobre el mismo clip devuelven el cache sin volver a transcribir. Si querés leer SÓLO lo que cae dentro del recorte actual del clip (mediaOffset + duration), usá después `get_clip_transcript`. Si querés ver todo lo que se dice en la timeline ya recortada, usá `get_timeline_transcript`. Soporta video/audio subido o ai-generated con audio listo. Idempotente: si el clip ya tiene transcript, devuelve el resumen cacheado al instante.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description: 'ID del clip de la timeline (debe ser video, audio, o ai-generated con audio listo). Usá list_timeline_clips para obtenerlo.',
        },
        force: {
          type: 'boolean',
          description: 'Si true, ignora el cache y vuelve a llamar al servicio de transcripción. Default: false. Usalo sólo si sospechás que el cache quedó desactualizado.',
        },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'get_clip_transcript',
    description:
      'Devuelve el transcript de UN clip específico, FILTRADO a la ventana actualmente visible (mediaOffset → mediaOffset + duration) — o sea, sólo lo que se dice en la parte del clip que efectivamente está en la timeline después de recortes/splits. Los timestamps se devuelven en segundos de timeline (no del archivo fuente), así podés referenciarlos directamente. Requiere que el clip ya tenga transcript: si no, llamá primero a `generate_transcript` o devolvé un error claro al usuario. Devuelve { clipId, name, startTime, endTime, durationVisible, text, words: [{ word, start, end }] }. Si la ventana visible no contiene ninguna palabra (ej. el clip se trimeó a un silencio), `words` viene vacío y `text` es "".',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'get_timeline_transcript',
    description:
      'Devuelve la transcripción combinada de TODA la timeline en orden cronológico, considerando los recortes/splits actuales — es decir, sólo las palabras que efectivamente se escuchan al reproducir el proyecto. Ignora clips silenciados (volume === 0) y clips sin transcript guardado. Devuelve un array de segmentos por clip { clipId, clipName, startTime, endTime, text, words } más un campo `fullText` con todo concatenado. Útil para preguntas tipo "qué dice el video", "encontrá donde se menciona X", "armá una bajada con lo que se dice", sin que te confundas con clips eliminados, recortes o duplicados. Si algún clip de video/audio importante no tiene transcript todavía, viene listado en `missingTranscriptClipIds` para que decidas si llamar a `generate_transcript` para esos clips.',
    input_schema: {
      type: 'object',
      properties: {
        includeMuted: {
          type: 'boolean',
          description: 'Si true, incluye también los clips silenciados. Default: false.',
        },
      },
    },
  },
  {
    name: 'list_generated_images',
    description:
      'Lista las imágenes generadas con IA guardadas en este proyecto (separadas de los assets subidos) y te las muestra DIRECTAMENTE como imágenes para que vos (Claude) las observes. Úsala cuando el usuario pregunte por imágenes generadas, quiera reutilizarlas como referencia, o necesites entender qué ya se creó. Devuelve el id de cada imagen junto con la imagen visual.',
    input_schema: {
      type: 'object',
      properties: {
        limit: {
          type: 'integer',
          minimum: 1,
          maximum: 20,
          description: 'Cantidad máxima de imágenes a devolver (las más recientes primero). Default: 12.',
        },
      },
    },
  },
  {
    name: 'import_generated_image_as_asset',
    description:
      'Convierte una imagen generada con IA (de las que devuelve list_generated_images) en un asset real del proyecto, listo para usarse como firstFrameAssetId, lastFrameAssetId o refAssetIds en prefill_ai_scene/prefill_ai_image, o para insertarse en la timeline. Devuelve el assetId nuevo. Llamala SIEMPRE antes de pasar una imagen generada como referencia/frame, porque las imágenes generadas usan ids de imagenes_generadas y no de assets.',
    input_schema: {
      type: 'object',
      properties: {
        generatedImageId: {
          type: 'string',
          description: 'ID devuelto por list_generated_images (campo imagen_id).',
        },
      },
      required: ['generatedImageId'],
    },
  },
  {
    name: 'extract_clip_frame_as_asset',
    description:
      "Extrae el primer o último frame de un clip de la timeline (video o ai-generated) y lo guarda como un asset de imagen del proyecto. Devuelve el assetId resultante, listo para usarse como firstFrameAssetId/lastFrameAssetId/refAssetIds. Útil para encadenar escenas (p.ej. usar el último frame de un clip como primer frame de la próxima escena IA).",
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip de la timeline.' },
        which: {
          type: 'string',
          enum: ['first', 'last'],
          description: "'first' = primer frame; 'last' = último frame.",
        },
      },
      required: ['clipId', 'which'],
    },
  },
  {
    name: 'list_available_voices',
    description:
      'Lista las voces IA disponibles para voz en off, con id, nombre, sexo, idioma, acento y una descripción. Úsala antes de llamar a prefill_ai_scene (modo personas) o prefill_ai_audio (type voice) cuando necesites elegir una voz adecuada al tono/público.',
    input_schema: {
      type: 'object',
      properties: {
        language: { type: 'string', description: "Filtro opcional por idioma (p.ej. 'es', 'en')." },
        sex: {
          type: 'string',
          enum: ['masculino', 'femenino'],
          description: 'Filtro opcional por sexo de la voz.',
        },
      },
    },
  },
  {
    name: 'get_brand_context',
    description:
      'Devuelve el brand_profile asociado al proyecto actual: id, name, context (texto libre con la memoria de la marca que se va acumulando), colors (paleta de hex strings) y fonts (objeto con la fuente asignada a cada rol jerárquico: antetitulo, titulo, subtitulo, cuerpo, detalles — cualquiera puede faltar). Llamala al principio para adaptar las propuestas al tono e identidad de la marca, y SIEMPRE antes de update_brand_context / update_brand_colors / update_brand_fonts para leer el estado actual y no pisar información.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'update_brand_context',
    description:
      "Actualiza el campo 'context' (texto libre) del brand_profile del proyecto actual. Usalo para recordar información relevante sobre la marca que surja en la conversación: descripción del negocio, tono, público, productos, preferencias visuales, reglas de comunicación, referencias, etc. El contexto es acumulativo: por defecto (mode='append') se agrega al texto existente en un nuevo párrafo. Usa mode='replace' SOLO si el usuario pide explícitamente reemplazar/resetear la memoria. SIEMPRE llamá a get_brand_context primero para no duplicar información.",
    input_schema: {
      type: 'object',
      properties: {
        context: {
          type: 'string',
          description:
            "Texto con la información a guardar. Si mode='append', este texto se agrega como nuevo párrafo al contexto existente. Si mode='replace', reemplaza todo el contexto actual.",
          minLength: 1,
        },
        mode: {
          type: 'string',
          enum: ['append', 'replace'],
          description: "'append' (default) agrega el texto como un nuevo párrafo. 'replace' sobreescribe todo.",
        },
      },
      required: ['context'],
    },
  },
  {
    name: 'update_brand_colors',
    description:
      "Actualiza la paleta de colores del brand_profile del proyecto actual. La paleta se guarda como un array de strings hex (ej: ['#1644eb','#ffffff']). Usá mode='add' para sumar colores nuevos sin pisar los existentes (los duplicados se descartan), mode='remove' para sacar colores específicos, o mode='replace' para reemplazar toda la paleta. SIEMPRE llamá a get_brand_context antes para conocer la paleta actual.",
    input_schema: {
      type: 'object',
      properties: {
        colors: {
          type: 'array',
          description: "Array de colores en formato hex con #, ej: ['#1644eb','#ff7700']. En mode='add' se agregan al final; en 'remove' se quitan los que coincidan; en 'replace' es la paleta completa nueva.",
          items: { type: 'string' },
          minItems: 1,
        },
        mode: {
          type: 'string',
          enum: ['add', 'remove', 'replace'],
          description: "'add' (default) suma sin duplicar. 'remove' quita los colores indicados. 'replace' sobreescribe la paleta entera.",
        },
      },
      required: ['colors'],
    },
  },
  {
    name: 'update_brand_fonts',
    description:
      "Asigna fuentes a los ROLES jerárquicos de la marca: antetitulo, titulo, subtitulo, cuerpo, detalles. Cada rol tiene UNA fuente (o ninguna). Las familias deben coincidir con las del catálogo del editor (sans/serif/display/handwriting). Usalo cuando el usuario expresa preferencia tipográfica para la marca ('para los títulos usá Anton', 'el cuerpo siempre Inter', 'sacá la fuente de los detalles'). Por defecto (mode='merge') solo modifica los roles que envíes y deja los demás como están; usá mode='replace' SOLO si el usuario pide redefinir todas las fuentes desde cero. Para LIMPIAR un rol específico, mandalo con valor null. SIEMPRE llamá a get_brand_context antes.",
    input_schema: {
      type: 'object',
      properties: {
        assignments: {
          type: 'object',
          description: "Mapa de rol → familia. Roles válidos: 'antetitulo', 'titulo', 'subtitulo', 'cuerpo', 'detalles'. El valor es el nombre de la familia (ej: 'Anton', 'Inter', 'Playfair Display'), o null para borrar la asignación de ese rol. Mandá solo los roles que querés tocar.",
          properties: {
            antetitulo: { type: ['string', 'null'] },
            titulo: { type: ['string', 'null'] },
            subtitulo: { type: ['string', 'null'] },
            cuerpo: { type: ['string', 'null'] },
            detalles: { type: ['string', 'null'] },
          },
          additionalProperties: false,
        },
        mode: {
          type: 'string',
          enum: ['merge', 'replace'],
          description: "'merge' (default) toca solo los roles enviados. 'replace' resetea TODOS los roles y deja únicamente los que mandes en `assignments`.",
        },
      },
      required: ['assignments'],
    },
  },
  {
    name: 'get_brand_assets',
    description:
      "Devuelve las imágenes guardadas en la sección Imágenes de la marca (logos + elementos visuales reutilizables) y te las muestra DIRECTAMENTE como imágenes para que vos (Claude) las observes. Cada item viene con: id, tipo ('logo' o 'element'), name, width, height, y la imagen visual. Usala SIEMPRE antes de prefillear cualquier escena/imagen IA donde tenga sentido incluir el logo o un elemento de marca (placas, badges, marcos), para saber qué tiene la marca disponible. Si la marca no tiene logos/elementos cargados pero el usuario los pide o los necesitarías, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba. Para usar uno de estos assets como referencia, primer/último frame o adjunto: el id devuelto NO es un assetId del proyecto — usalo con import_brand_asset_to_project para convertirlo en un asset del proyecto antes de pasarlo a prefill_*.",
    input_schema: {
      type: 'object',
      properties: {
        tipo: {
          type: 'string',
          enum: ['logo', 'element'],
          description: "Filtrá por tipo. 'logo' = versiones del logo de la marca. 'element' = elementos visuales reutilizables (badges, iconos, marcos, ilustraciones). Omitir para listar ambos.",
        },
      },
    },
  },
  {
    name: 'import_brand_asset_to_project',
    description:
      'Convierte una imagen de la marca (logo o elemento, devuelta por get_brand_assets) en un asset del proyecto actual, listo para usarse como firstFrameAssetId, lastFrameAssetId o refAssetIds en prefill_ai_scene/prefill_ai_image. Devuelve el assetId nuevo. Llamala SIEMPRE antes de pasar una imagen de marca como referencia, porque las imágenes de marca viven en otra tabla y otro bucket que los assets del proyecto.',
    input_schema: {
      type: 'object',
      properties: {
        brandAssetId: {
          type: 'string',
          description: 'ID devuelto por get_brand_assets (campo id).',
        },
      },
      required: ['brandAssetId'],
    },
  },
  {
    name: 'open_brand_panel',
    description:
      "Abre el panel de Marca en una pestaña específica para que el usuario complete información que falta. Usala cuando notes que la marca no tiene cargado algo que necesitarías (logos, elementos visuales, fuentes, colores, memoria) y querés que el usuario lo suba/escriba. Después de llamar a esta tool, RESPONDÉ AL USUARIO en una frase corta indicándole qué tiene que hacer en el panel (ej: 'Te abrí la pestaña Imágenes de tu marca — subí el logo principal así puedo usarlo en los videos'). Pestañas disponibles: 'memoria' (texto libre con el contexto de la marca), 'fuentes' (asignación tipográfica por rol), 'colores' (paleta), 'imagenes' (logos + elementos visuales).",
    input_schema: {
      type: 'object',
      properties: {
        tab: {
          type: 'string',
          enum: ['memoria', 'fuentes', 'colores', 'imagenes'],
          description: 'Pestaña que querés mostrarle al usuario.',
        },
      },
      required: ['tab'],
    },
  },
  {
    name: 'get_ui_context',
    description:
      'Devuelve el estado actual de la UI del editor: qué panel de la sidebar está abierto (activePanel), qué clip está seleccionado en la timeline (si alguno), el aspect ratio del proyecto, y las opciones/campos actualmente cargados en el panel abierto (prompt, duración, modo, voz, referencias, etc.). Usala antes de proponer cambios o llamar a prefill_* para saber qué hay ya cargado y no pisar trabajo del usuario, o para entender el contexto visual cuando el usuario dice algo como "cambiale la duración" sin especificar.',
    input_schema: { type: 'object', properties: {} },
  },

  // ── UI pre-fill (no auto-generation; user must confirm) ───────────────
  {
    name: 'prefill_ai_scene',
    description:
      "Abre el panel 'Nueva escena con IA' en la UI y pre-carga TODOS los campos con los valores propuestos. NO genera el video automáticamente: el usuario revisa los parámetros y aprieta 'Generar' o 'Borrador'. Usala cuando el usuario pide crear una escena de video con IA. Soporta DOS modos: (A) Single — pasá `prompt` + `duration` para una sola escena. (B) Multi-escena — pasá `scenes` (array de 2-3 ítems con prompt+duration cada uno) para encadenar varias escenas contínuas en la misma generación, manteniendo coherencia de personajes y estilo. El modo multi sólo vale con sceneMode='general' y requiere al menos una imagen en refAssetIds (la misma referencia aplica a todas las escenas). Suma de duraciones del multi ≤ 15s.",
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'Descripción visual detallada de la escena (encuadre, acción, iluminación, estilo, mood). Obligatorio en modo single. Ignorado si pasás `scenes`.',
        },
        sceneMode: {
          type: 'string',
          enum: ['general', 'personas'],
          description:
            "'general' para escenas sin habla. 'personas' para escenas con una persona hablando (requiere voiceId y dialogue). En modo 'personas' leé también `personType`: el comportamiento del prompt cambia entre static y motion.",
        },
        personType: {
          type: 'string',
          enum: ['static', 'motion'],
          description:
            "Sólo aplica si sceneMode='personas'. Default: 'static'. \n'static' (recomendado, default): la persona habla MIRANDO A CÁMARA con poco movimiento (UGC tipo testimonial). En este modo NO hay descripción visual separada — el campo `dialogue` ES la única entrada de texto, contiene únicamente lo que la persona dice. NO pases `prompt` en static (se ignora). Requiere `firstFrameAssetId` (foto de la persona).\n'motion': la persona habla MIENTRAS hace algo / camina / interactúa con el producto (más cinematográfico). Acá SÍ pasás los dos: `prompt` (qué pasa visualmente) Y `dialogue` (qué dice). Más caro y lento; usalo sólo cuando el movimiento aporta.",
        },
        duration: {
          type: 'number',
          description: 'Duración en segundos. El modelo de generación de video soporta entre 4 y 15 segundos por escena.',
          minimum: 4,
          maximum: 15,
        },
        aspectRatio: {
          type: 'string',
          enum: ['16:9', '9:16', '1:1', '4:5'],
          description: 'Proporción de la escena. Si no se especifica, usa el del proyecto.',
        },
        quality: {
          type: 'string',
          enum: ['rapido', 'estandar'],
          description: "Calidad de render. Default: 'estandar'.",
        },
        resolution: {
          type: 'string',
          enum: ['480p', '720p', '1080p'],
          description: "Resolución final. Default: '720p'. Dialog mode no soporta 480p.",
        },
        voiceId: {
          type: 'string',
          description:
            "ID de la voz IA a usar. Sólo tiene sentido si sceneMode='personas'. Elegir con list_available_voices.",
        },
        dialogue: {
          type: 'string',
          description: "Texto literal que dirá la persona en la escena. Sólo si sceneMode='personas'.",
        },
        speechSpeed: {
          type: 'number',
          description: "Velocidad de habla (0.5 lenta … 1.5 rápida). Default: 1.1. Sólo si sceneMode='personas'.",
          minimum: 0.5,
          maximum: 1.5,
        },
        firstFrameAssetId: {
          type: 'string',
          description: 'ID de un asset tipo imagen del proyecto a usar como primer frame.',
        },
        lastFrameAssetId: {
          type: 'string',
          description: 'ID de un asset tipo imagen del proyecto a usar como último frame.',
        },
        refAssetIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'IDs de imágenes del proyecto como referencias visuales/estilo. En modo multi-escena es obligatorio al menos 1.',
          maxItems: 4,
        },
        scenes: {
          type: 'array',
          description:
            "Si se pasa, activa modo multi-escena (sceneMode debe ser 'general' y refAssetIds debe tener al menos 1). Cada ítem es una escena corta contínua. `prompt`/`duration` del top-level se ignoran. Cada duración entre 4 y 10 segundos; la suma total no puede superar 15.",
          items: {
            type: 'object',
            properties: {
              prompt: {
                type: 'string',
                description: 'Descripción visual de esta escena específica (qué pasa, encuadre, acción).',
              },
              duration: {
                type: 'number',
                minimum: 4,
                maximum: 10,
                description: 'Duración de esta escena en segundos (entre 4 y 10).',
              },
            },
            required: ['prompt', 'duration'],
          },
          minItems: 2,
          maxItems: 3,
        },
      },
      required: ['sceneMode'],
    },
  },
  {
    name: 'prefill_ai_audio',
    description:
      "Abre el panel 'Nuevo audio con IA' y pre-carga los campos. NO genera nada: el usuario aprieta 'Generar'. Usala para proponer una voz en off o una pieza musical.",
    input_schema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['voice', 'music'],
          description: "'voice' para voz en off narrada. 'music' para generar música.",
        },
        prompt: {
          type: 'string',
          description:
            "Si type='voice': el texto literal que dirá la voz. Si type='music': descripción del estilo/género/mood/instrumentos.",
        },
        duration: { type: 'number', description: 'Duración en segundos.', minimum: 2, maximum: 60 },
        voiceId: {
          type: 'string',
          description:
            "ID de la voz IA. Obligatorio si type='voice'. Elegir con list_available_voices.",
        },
        speechSpeed: {
          type: 'number',
          description: "Velocidad de habla (0.5..1.5). Default: 1.1. Sólo si type='voice'.",
          minimum: 0.5,
          maximum: 1.5,
        },
        instrumentalOnly: {
          type: 'boolean',
          description: "Sólo si type='music'. True = sin voces ni letras. Default: false.",
        },
      },
      required: ['type', 'prompt', 'duration'],
    },
  },
  {
    name: 'prefill_ai_image',
    description:
      "Abre el panel de generación de imagen IA y pre-carga el prompt + referencias. NO genera: el usuario aprieta 'Generar'. Usala cuando el usuario pide una imagen estática (thumbnail, poster, referencia visual, primer/último frame).",
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Descripción visual de la imagen.' },
        aspectRatio: {
          type: 'string',
          enum: ['16:9', '9:16', '1:1', '4:5'],
          description: 'Proporción de la imagen.',
        },
        refAssetIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'IDs de imágenes del proyecto como referencia de estilo/composición.',
          maxItems: 4,
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'prefill_edit_generated_image',
    description:
      "Abre una imagen previamente generada en MODO EDICIÓN del panel de imagen IA y pre-llena el prompt de cambio. NO ejecuta el cambio: el usuario revisa y aprieta 'Editar'. Usala cuando el usuario quiere modificar una imagen que ya generó (ej: 'hacela más oscura', 'cambiale el fondo'). Pasale el id de la imagen (de list_generated_images) y la descripción del cambio.",
    input_schema: {
      type: 'object',
      properties: {
        generatedImageId: {
          type: 'string',
          description: 'ID de la imagen generada (campo id de list_generated_images).',
        },
        editPrompt: {
          type: 'string',
          description: 'Descripción del cambio a aplicar (ej: "hacela en blanco y negro").',
          minLength: 1,
        },
      },
      required: ['generatedImageId', 'editPrompt'],
    },
  },
  {
    name: 'prefill_edit_scene_frame',
    description:
      "Abre el primer o último frame actualmente cargado en el panel de escena IA en una pantalla de preview con su prompt de edición pre-llenado. NO ejecuta el cambio: el usuario aprieta 'Editar' y se genera una nueva imagen con el frame original como referencia. Usala cuando el usuario quiere modificar el frame (ej: 'al primer frame ponele un atardecer'). Requiere que el panel ai-scene esté en modo 'frames' y que el slot ya tenga una imagen.",
    input_schema: {
      type: 'object',
      properties: {
        which: {
          type: 'string',
          enum: ['first', 'last'],
          description: "Qué frame editar: 'first' (primer frame) o 'last' (último frame).",
        },
        editPrompt: {
          type: 'string',
          description: 'Descripción del cambio a aplicar al frame.',
          minLength: 1,
        },
      },
      required: ['which', 'editPrompt'],
    },
  },
  {
    name: 'nudge_image_slot',
    description:
      "Hace saltar (3 veces) una zona del panel donde el usuario debe subir o agregar una imagen, para guiar visualmente la atención. Usala SÓLO cuando ya le pediste por chat al usuario que adjunte/suba una imagen y querés resaltar EXACTAMENTE dónde tiene que hacerlo. Requiere que el panel correspondiente esté abierto (prefill_ai_scene o prefill_ai_image antes).",
    input_schema: {
      type: 'object',
      properties: {
        slots: {
          type: 'array',
          items: {
            type: 'string',
            enum: ['scene-firstframe', 'scene-lastframe', 'scene-ref', 'image-attach'],
          },
          minItems: 1,
          maxItems: 4,
          description:
            "Qué zona(s) hacer saltar. 'scene-firstframe'/'scene-lastframe' son los slots de primer/último frame en el panel de escena IA. 'scene-ref' es el botón 'Añadir imagen' de referencia en el panel de escena IA. 'image-attach' es el botón 'Añadir' del panel de generar imagen IA.",
        },
      },
      required: ['slots'],
    },
  },

  // ── Timeline editing (direct, reversible) ──────────────────────────────
  {
    name: 'add_text_clip',
    description:
      'Agrega un clip de texto directamente en la timeline (no requiere confirmación del usuario: es reversible con Undo). Podés configurar tipografía, tamaño, color, alineación y posición. Si omitís campos tipográficos, se usan defaults sensatos o el preset indicado en `style`.',
    input_schema: {
      type: 'object',
      properties: {
        text: { type: 'string', description: 'Texto a mostrar.' },
        style: {
          type: 'string',
          enum: ['titulo', 'subtitulo', 'cuerpo', 'animado'],
          description: 'Preset opcional de estilo (define tamaño/peso base si no se pasan explícitos).',
        },
        startTime: {
          type: 'number',
          description:
            'Tiempo en segundos donde insertar. Si se omite, se agrega al final de la pista de texto.',
        },
        duration: {
          type: 'number',
          description: 'Duración en segundos. Default: 3.',
          minimum: 0.5,
          maximum: 60,
        },
        fontFamily: {
          type: 'string',
          description: 'Familia tipográfica (ej: "Inter", "Poppins", "Montserrat", "Playfair Display", "Bebas Neue", "Oswald", "Lobster", "Caveat", "Pacifico"). Default: "Inter".',
        },
        fontSizePct: {
          type: 'number',
          description: 'Tamaño como porcentaje de la altura del canvas (2–30). Default: 8.',
          minimum: 2,
          maximum: 30,
        },
        fontWeight: {
          type: 'string',
          enum: ['normal', 'bold'],
          description: 'Peso tipográfico. Default: "normal".',
        },
        fontStyle: {
          type: 'string',
          enum: ['normal', 'italic'],
          description: 'Estilo tipográfico. Default: "normal".',
        },
        textColor: {
          type: 'string',
          description: 'Color del texto en hexadecimal (ej: "#FFFFFF"). Default: "#FFFFFF".',
        },
        textAlign: {
          type: 'string',
          enum: ['left', 'center', 'right'],
          description: 'Alineación horizontal. Default: "center".',
        },
        textXPct: {
          type: 'number',
          description: 'Posición horizontal del centro del texto, 0–100 % del ancho del canvas. Default: 50.',
          minimum: 0,
          maximum: 100,
        },
        textYPct: {
          type: 'number',
          description: 'Posición vertical del centro del texto, 0–100 % del alto del canvas. Default: 50.',
          minimum: 0,
          maximum: 100,
        },
      },
      required: ['text'],
    },
  },
  {
    name: 'add_asset_to_timeline',
    description:
      'Inserta un asset existente del proyecto (video/audio) en la timeline. Usá list_project_assets primero para obtener el assetId.',
    input_schema: {
      type: 'object',
      properties: {
        assetId: { type: 'string', description: 'ID del asset a insertar.' },
        startTime: {
          type: 'number',
          description: 'Tiempo en segundos. Si se omite, se agrega al final de la pista apropiada.',
        },
        trackIndex: {
          type: 'integer',
          description: 'Índice de pista (0 = principal). Default: 0.',
          minimum: 0,
        },
      },
      required: ['assetId'],
    },
  },
  {
    name: 'move_clip',
    description: 'Cambia la posición (startTime) y opcionalmente la pista (trackIndex) de un clip existente.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'ID del clip a mover.' },
        startTime: { type: 'number', description: 'Nueva posición en segundos.', minimum: 0 },
        trackIndex: {
          type: 'integer',
          description: 'Nueva pista. Omitir para mantener la actual.',
          minimum: 0,
        },
      },
      required: ['clipId', 'startTime'],
    },
  },
  {
    name: 'trim_clip',
    description:
      'Ajusta la duración de un clip y opcionalmente su mediaOffset (desde qué punto del material original empieza).',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        duration: { type: 'number', description: 'Nueva duración en segundos.', minimum: 0.1 },
        mediaOffset: {
          type: 'number',
          description: 'Segundos a saltar al inicio del material original. Default: sin cambio.',
          minimum: 0,
        },
      },
      required: ['clipId', 'duration'],
    },
  },
  {
    name: 'split_clip',
    description: 'Divide un clip en dos en un tiempo dado (relativo al inicio del clip).',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        splitAt: {
          type: 'number',
          description: 'Segundos desde el inicio del clip donde dividir (debe ser > 0.1 y < duration - 0.1).',
        },
      },
      required: ['clipId', 'splitAt'],
    },
  },
  {
    name: 'delete_clip',
    description: 'Elimina un clip de la timeline.',
    input_schema: {
      type: 'object',
      properties: { clipId: { type: 'string' } },
      required: ['clipId'],
    },
  },
  {
    name: 'autocut_interview',
    description:
      "Corta automáticamente las muletillas (eh, este, o sea, mmm, …) y/o los silencios largos de un clip de video con voz, usando su transcripción. El clip se reconstruye más corto (los pedazos que se conservan quedan pegados) y los clips siguientes de SU pista se corren para cerrar el hueco; otras pistas no se tocan. FLUJO OBLIGATORIO: corré primero con dry_run=true para obtener cuántos cortes y cuántos segundos se sacarían, mostráselo al usuario y pedí confirmación; recién entonces volvé a llamar con dry_run=false para aplicar. Requiere que el clip tenga transcript (si no, llamá generate_transcript primero).",
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string', description: 'Id del clip (de list_timeline_clips).' },
        filler_words: {
          type: 'array',
          items: { type: 'string' },
          description: 'Override de la lista de muletillas (lowercase, sin acentos). Default: lista en español.',
        },
        max_silence_seconds: {
          type: 'number',
          minimum: 0.3,
          maximum: 10,
          description: 'Gap entre palabras mayor a esto = silencio a cortar. Default 2.5 (conservador; bajá a 1.0-1.5 para más agresivo).',
        },
        padding_seconds: {
          type: 'number',
          minimum: 0,
          maximum: 0.6,
          description: 'Margen de seguridad alrededor de cada corte. Default 0.3.',
        },
        remove_filler_words: { type: 'boolean', description: 'Si false, solo corta silencios. Default true.' },
        remove_silences: { type: 'boolean', description: 'Si false, solo corta muletillas. Default true.' },
        dry_run: { type: 'boolean', description: 'Si true, solo calcula y devuelve el resumen sin tocar el timeline. Default false.' },
      },
      required: ['clipId'],
    },
  },
  {
    name: 'erase_object',
    description:
      'Borra un objeto del video de un clip (magic eraser): saca un objeto descrito por texto y rellena el fondo de forma temporalmente consistente (Bria). Ej: "sacá los cables", "sacá la taza de la mesa". Requiere un clip de VIDEO ya subido a la nube. CUESTA CRÉDITOS y tarda varios minutos (procesa por segmentos). REGLAS: 1) Confirmá SIEMPRE con ask_user mostrando el costo estimado antes de disparar. 2) Si el clip dura más de 60s, avisá que va a tardar más y tiene más riesgo de costura, y pedí confirmación; recién ahí llamá con confirm_long_clip:true. Mientras procesa, el clip muestra un shimmer en la timeline; cuando termina se reemplaza in-place y el resultado queda también en Archivos.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description: 'Id del clip de video (de list_timeline_clips).',
        },
        prompt: {
          type: 'string',
          description: 'Descripción concreta del objeto a borrar, ej: "los cables del fondo", "la taza sobre la mesa".',
        },
        confirm_long_clip: {
          type: 'boolean',
          description: 'Poné true solo después de que el usuario confirmó procesar un clip de más de 60s.',
        },
      },
      required: ['clipId', 'prompt'],
    },
  },
  {
    name: 'set_clip_volume',
    description:
      'Cambia el volumen de un clip de audio/video. Use 0 para silenciar (aparece badge en la UI).',
    input_schema: {
      type: 'object',
      properties: {
        clipId: { type: 'string' },
        volume: {
          type: 'number',
          description: 'Volumen: 0 = mute, 1 = normal, 2 = máximo.',
          minimum: 0,
          maximum: 2,
        },
      },
      required: ['clipId', 'volume'],
    },
  },
  {
    name: 'translate_clip_to_english',
    description:
      'Dobla un clip de video al inglés con lip-sync, manteniendo la voz del speaker original (vía HeyGen Video Translate v3). Solo aplica a clips type="video" o type="ai-generated" en estado "ready". Es asíncrono: el flujo arranca el job en HeyGen y devuelve inmediatamente — el clip queda con aiTranslating=true mientras procesa (puede tardar 5-15 min) y se REEMPLAZA in-place en la timeline cuando termina (mismo startTime/trackIndex, nuevo src y posiblemente nueva duración). Cuesta 0.3 créditos por segundo de duración del clip (mínimo 1 crédito). NO disparar sin confirmación previa del usuario — preguntá primero con ask_user mostrando el costo estimado.',
    input_schema: {
      type: 'object',
      properties: {
        clipId: {
          type: 'string',
          description:
            'ID del clip de video a traducir. Debe ser type="video" o type="ai-generated" con aiStatus="ready" y tener src cargado.',
        },
      },
      required: ['clipId'],
    },
  },

  // ── Project metadata ────────────────────────────────────────────────────
  {
    name: 'set_project_name',
    description: 'Renombra el proyecto.',
    input_schema: {
      type: 'object',
      properties: { name: { type: 'string', minLength: 1, maxLength: 120 } },
      required: ['name'],
    },
  },
  {
    name: 'set_project_aspect_ratio',
    description:
      'Cambia el aspect ratio global del proyecto. Afecta la preview y los defaults de nuevas escenas IA.',
    input_schema: {
      type: 'object',
      properties: {
        aspectRatio: { type: 'string', enum: ['16:9', '9:16', '1:1', '4:5'] },
      },
      required: ['aspectRatio'],
    },
  },
];
